// Video caching utilities
// Handles caching of video data and thumbnails to improve performance

export class VideoCache {
  constructor() {
    this.cachePrefix = 'fcco_videos_';
    this.imageCachePrefix = 'fcco_img_';
    this.cacheExpiry = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
  }

  // Cache video data
  cacheVideoData(channelId, videos) {
    const cacheData = {
      timestamp: Date.now(),
      channelId,
      videos,
      version: '1.0'
    };
    
    try {
      localStorage.setItem(
        `${this.cachePrefix}${channelId}`, 
        JSON.stringify(cacheData)
      );
      console.log('✅ Videos cached successfully');
    } catch (error) {
      console.warn('⚠️ Failed to cache video data:', error);
    }
  }

  // Get cached video data
  getCachedVideoData(channelId) {
    try {
      const cachedData = localStorage.getItem(`${this.cachePrefix}${channelId}`);
      if (!cachedData) return null;

      const parsed = JSON.parse(cachedData);
      
      // Check if cache is expired
      if (Date.now() - parsed.timestamp > this.cacheExpiry) {
        console.log('🕒 Cache expired, removing old data');
        this.clearVideoCache(channelId);
        return null;
      }

      console.log('✅ Using cached video data');
      return parsed.videos;
    } catch (error) {
      console.warn('⚠️ Failed to retrieve cached data:', error);
      return null;
    }
  }

  // Clear video cache
  clearVideoCache(channelId = null) {
    if (channelId) {
      localStorage.removeItem(`${this.cachePrefix}${channelId}`);
    } else {
      // Clear all video caches
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith(this.cachePrefix)) {
          localStorage.removeItem(key);
        }
      });
    }
  }

  // Cache image data as base64
  async cacheImage(url, videoId) {
    try {
      // Check if already cached
      const cachedImage = this.getCachedImage(videoId);
      if (cachedImage) return cachedImage;

      const response = await fetch(url);
      const blob = await response.blob();
      
      // Convert to base64 for localStorage
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64Data = reader.result;
          
          const cacheData = {
            timestamp: Date.now(),
            data: base64Data,
            videoId
          };

          try {
            localStorage.setItem(
              `${this.imageCachePrefix}${videoId}`, 
              JSON.stringify(cacheData)
            );
            console.log(`✅ Image cached for video: ${videoId}`);
          } catch (error) {
            console.warn('⚠️ Failed to cache image:', error);
          }

          resolve(base64Data);
        };
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.warn('⚠️ Failed to cache image:', error);
      return url; // Return original URL as fallback
    }
  }

  // Get cached image
  getCachedImage(videoId) {
    try {
      const cachedData = localStorage.getItem(`${this.imageCachePrefix}${videoId}`);
      if (!cachedData) return null;

      const parsed = JSON.parse(cachedData);
      
      // Check if cache is expired (images expire after 7 days)
      const imageExpiry = 7 * 24 * 60 * 60 * 1000; // 7 days
      if (Date.now() - parsed.timestamp > imageExpiry) {
        localStorage.removeItem(`${this.imageCachePrefix}${videoId}`);
        return null;
      }

      return parsed.data;
    } catch (error) {
      console.warn('⚠️ Failed to retrieve cached image:', error);
      return null;
    }
  }

  // Process video data and cache images
  async processAndCacheVideos(videos) {
    const processedVideos = [];

    for (const video of videos) {
      const processedVideo = { ...video };
      
      // Extract YouTube video ID from various sources
      const youtubeId = this.extractYouTubeId(video);
      if (youtubeId) {
        processedVideo.youtubeId = youtubeId;
        
        // Get thumbnail URL
        const thumbnailUrl = this.getBestThumbnailUrl(video.snippet?.thumbnails, youtubeId);
        
        // Cache the thumbnail image
        const cachedImageUrl = await this.cacheImage(thumbnailUrl, youtubeId);
        processedVideo.thumbnailUrl = cachedImageUrl;
        
        // Add other processed fields
        processedVideo.title = video.snippet?.title || 'Untitled Video';
        processedVideo.description = video.snippet?.description || '';
        processedVideo.publishedAt = video.snippet?.publishedAt || '';
      }

      processedVideos.push(processedVideo);
    }

    return processedVideos;
  }

  // Extract YouTube video ID from different API response formats
  extractYouTubeId(video) {
    // From search API
    if (video.id?.videoId) return video.id.videoId;
    
    // From videos API
    if (typeof video.id === 'string') return video.id;
    
    // From custom format
    if (video.youtubeId) return video.youtubeId;
    
    return null;
  }

  // Get the best available thumbnail URL
  getBestThumbnailUrl(thumbnails, youtubeId) {
    if (thumbnails) {
      // Prefer higher quality thumbnails
      if (thumbnails.maxres) return thumbnails.maxres.url;
      if (thumbnails.high) return thumbnails.high.url;
      if (thumbnails.medium) return thumbnails.medium.url;
      if (thumbnails.standard) return thumbnails.standard.url;
      if (thumbnails.default) return thumbnails.default.url;
    }
    
    // Fallback to YouTube's standard thumbnail URL
    return `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`;
  }

  // Get cache statistics
  getCacheStats() {
    let videoCount = 0;
    let imageCount = 0;
    let totalSize = 0;

    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(this.cachePrefix)) videoCount++;
      if (key.startsWith(this.imageCachePrefix)) imageCount++;
      
      const value = localStorage.getItem(key);
      if (value) totalSize += value.length;
    });

    return {
      videoCount,
      imageCount,
      totalSize: (totalSize / 1024 / 1024).toFixed(2) + ' MB'
    };
  }

  // Clear all caches
  clearAllCaches() {
    this.clearVideoCache();
    
    // Clear image caches
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(this.imageCachePrefix)) {
        localStorage.removeItem(key);
      }
    });
    
    console.log('🗑️ All video caches cleared');
  }
}

// Create singleton instance
export const videoCache = new VideoCache();
